import {
	Box,
	Flex,
	type FlexProps,
	For,
	Progress,
	type ProgressTrackProps,
	Text,
} from '@chakra-ui/react';
import type React from 'react';
import { useState } from 'react';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';

export interface StepperStep {
	name: string;
	element: React.ReactNode;
}

export interface StepperProps extends Omit<FlexProps, 'children'> {
	title?: string;
	steps: StepperStep[];
	controlButtonsPosition?: 'left' | 'right';
	onStepChange: (step: number) => void;
	stepOverride?: number;
	submitButtonText?: string;
	onSubmit?: () => void;
	validateStep?: (step: number) => Promise<boolean>;
	isNextDisabled?: boolean;
	rightSideButtons?: (
		currentStep: number,
		totalSteps: number,
	) => React.ReactNode;
	loading?: boolean;
}

export interface ProgressTrackProps2 extends ProgressTrackProps {
	children?: React.ReactNode;
}

const CustomProgress = {
	Root: (props: React.ComponentProps<typeof Progress.Root>) => (
		<Progress.Root {...props} />
	),
	Track: (props: ProgressTrackProps2) => <Progress.Track {...props} />,
	Range: (props: React.ComponentProps<typeof Progress.Range>) => (
		<Box as={Progress.Range} bg="brand.solidGradient" {...props} />
	),
};

const Stepper: React.FC<StepperProps> = ({
	title,
	steps = [],
	controlButtonsPosition = 'left',
	onStepChange,
	stepOverride,
	submitButtonText,
	onSubmit,
	rightSideButtons,
	validateStep,
	loading,
	isNextDisabled,
	...rest
}) => {
	const TOTAL_STEPS = steps.length;
	const [activeStep, setActiveStep] = useState(1);

	// Sync internal activeStep with external override when provided
	if (
		typeof stepOverride === 'number' &&
		stepOverride >= 1 &&
		stepOverride <= TOTAL_STEPS &&
		stepOverride !== activeStep
	) {
		setActiveStep(stepOverride);
	}

	const handleNext = async () => {
		if (validateStep && !(await validateStep(activeStep))) {
			return; // Don't proceed if validation fails
		}
		setActiveStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
		onStepChange(Math.min(activeStep + 1, TOTAL_STEPS));
	};

	const handleBack = () => {
		setActiveStep((prev) => Math.max(prev - 1, 1));
		onStepChange(Math.max(activeStep - 1, 1));
	};

	const defaultStepCounterFormat = () =>
		`Step ${activeStep}/${TOTAL_STEPS}`;

	return (
		<Flex direction="column" gap={4} width="full" p={2} {...rest}>
			<Show when={!!title}>
				<FaceliftText fontSize="xl" fontWeight="bold">
					{title}
				</FaceliftText>
			</Show>

			<Flex width="50%" smDown={{ width: '100%' }} align="center" gap={3}>
				<Box flex="1" mt={-7}>
					<CustomProgress.Root
						width="100%"
						value={(activeStep / TOTAL_STEPS) * 100}
						size="xs"
						colorPalette="brand"
					>
						<CustomProgress.Track>
							<CustomProgress.Range />
						</CustomProgress.Track>
					</CustomProgress.Root>
				</Box>
				<Text fontSize="xs" color="gray.500" mt={-7} alignSelf="center">
					{defaultStepCounterFormat()}
				</Text>
			</Flex>

			<Flex direction="column" gap={10}>
				{/* Children */}
				<Box>
					<For each={steps}>
						{(step, index) => (
							<Show when={activeStep === index + 1} key={index}>
								{step.element}
							</Show>
						)}
					</For>
				</Box>
				<Flex
					direction={{ base: 'column', sm: 'row' }}
					gap={{ base: 2, md: 3 }}
					justifyContent={
						rightSideButtons
							? 'space-between'
							: controlButtonsPosition === 'left'
								? 'start'
								: 'end'
					}
					width="full"
					align={{ base: 'stretch', sm: 'center' }}
				>
					<Flex
						gap={{ base: 2, md: 3 }}
						direction={{ base: 'column', sm: 'row' }}
						width={{ base: 'full', sm: 'auto' }}
					>
						<Show when={activeStep > 1}>
							<FaceliftButton
								width={{ base: 'full', sm: '24', md: '28', lg: '40' }}
								bg="transparent"
								color="inherit"
								border="1px solid"
								onClick={handleBack}
								loading={loading}
								disabled={loading}
							>
								Back
							</FaceliftButton>
						</Show>

						<FaceliftButton
							width={{ base: 'full', sm: '24', md: '28', lg: '40' }}
							onClick={activeStep === TOTAL_STEPS ? onSubmit : handleNext}
							type="submit"
							loading={loading}
							disabled={loading || isNextDisabled}
						>
							<Show when={activeStep < TOTAL_STEPS}>Next</Show>
							<Show when={activeStep === TOTAL_STEPS}>
								{submitButtonText || 'Submit'}
							</Show>
						</FaceliftButton>
					</Flex>

					{rightSideButtons && (
						<Box width={{ base: 'full', sm: 'auto' }}>
							{rightSideButtons(activeStep, TOTAL_STEPS)}
						</Box>
					)}
				</Flex>
			</Flex>
		</Flex>
	);
};

export default Stepper;
